import { LinkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { formatDateStr } from '../../../../common-utils/formatters';
import type { CompanyBailExtended } from '../../../@types/Copro';
import { fetchBauxCommerciauxAEcheance } from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { BauxDict } from '../../../utils/dictionnayMap';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import Popup from '../../common/Popup';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';
import WarningValidationPending from '../../common/WarningValidationPending';

const headers = [
  { title: 'SIREN' },
  { title: 'Nom SIREN' },
  { title: 'Adresse' },
  { title: 'Nom de la copro' },
  { title: 'Gestionnaire' },
  { title: 'Date potentielle de période triennale' },
  { title: 'Voir le détail', class: 'min-w-[120px]' },
];

const sortableColumns = [
  'SIREN', 'Nom SIREN', 'Adresse', 'Nom de la copro', 'Gestionnaire', 'Date potentielle de période triennale',
];

function BauxAEcheance() {
  const dispatch = useAppDispatch();
  const jwt = useAppSelector((state) => state.auth.token);
  const company = useAppSelector((state) => state.auth.company);
  const baux = useAppSelector((state) => state.copro.bauxCommerciauxAEcheance);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = useState<number | null>(null);

  useEffect(() => {
    if (company && company.id && jwt) {
      dispatch(fetchBauxCommerciauxAEcheance());
    }
  }, [company, dispatch, jwt]);

  const handleDetails = (index: number) => {
    setIsPopupOpen(true);
    setSelectedRowDetails(index);
  };

  const renderNestedObject = (obj: Record<string, unknown>, parentKey = '') => (
    <MUITable
      data={Object.keys(obj).map((key) => {
        const value = obj[key];
        const displayKey = parentKey ? `${parentKey}.${key}` : key;
        const displayLabel = BauxDict[key] ? BauxDict[key] : key;
        if (typeof value === 'object' && value !== null) {
          const objValue = value as Record<string, unknown>;
          return {
            Libellé: <strong>{displayLabel}</strong>,
            Valeur: (
              <div key={displayKey}>
                {key === 'gestionnaireCopro'
                  ? (objValue.email ? `${String(objValue.firstname ?? '')} ${objValue.lastname ? String(objValue.lastname).toUpperCase() : ''} - ${String(objValue.email ?? '')}` : 'N/A')
                  : renderNestedObject(objValue, displayKey)}
              </div>
            ),
          };
        }
        return {
          Libellé: <strong>{displayLabel}</strong>,
          Valeur: (
            <div key={displayKey}>{value as React.ReactNode}</div>
          ),
        };
      })}
      headers={[
        { title: 'Libellé', class: 'w-1/2', customContentClass: 'w-1/2' },
        { title: 'Valeur', class: 'w-1/2', customContentClass: 'w-1/2 break-words break-all whitespace-normal' },
      ]}
      isSearchable={false}
      disablePagination
      tableClass="table-fixed"
    />
  );

  const renderPopupTitle = (selectedIndex: number | null) => {
    if (selectedIndex === null || !baux || !baux[selectedIndex]) {return "Détail de l'entreprise";}
    
    const siren = baux[selectedIndex].siren;
    
    return (
      <div className="items-center md:flex md:justify-center">
        <span>Détail de l'entreprise</span>
        {siren && (
          <a 
            href={`https://www.pappers.fr/entreprise/${siren}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 ml-0 flex w-full items-center rounded bg-rgpd-blue-dark px-3 py-2 text-sm text-white shadow-md md:mt-0 md:ml-4 md:w-auto"
          >
            <LinkIcon className="mr-1 h-4 w-4" />
            Ouvrir dans Pappers
          </a>
        )}
      </div>
    );
  };
  
  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <WarningValidationPending />
          <p>
            L'outil "
            <span className="font-bold text-rgpd-blue-light">Baux à échéance</span>
            " est essentiel pour les gestionnaires immobiliers. Cette section permet de suivre de près les
            {' '}
            <span className="font-bold text-rgpd-blue-light">dates potentielle de période triennale</span>
            {' '}suggérant ainsi les baux à échéance.
            <br />
            {' '}
            Grâce à cette surveillance, vous pouvez mieux anticiper les fins de période de location et informer les propriétaires concernés. L'identification proactive des opportunités de renouvellement de baux permet une gestion plus efficace et sécurisée de vos actifs immobiliers.
          </p>
        </div>
        <div>
          <div className="w-full overflow-x-scroll">
            <MUITable
              data={baux ? baux.map((bauxEntry: CompanyBailExtended, index) => {
                const address = [
                  bauxEntry.numeroVoieEtablissement,
                  bauxEntry.typeVoieEtablissement,
                  bauxEntry.libelleVoieEtablissement,
                  bauxEntry.codePostalEtablissement,
                  bauxEntry.libelleCommuneEtablissement,
                ].join(' ');
                return {
                  SIREN: bauxEntry.siren,
                  'Nom SIREN': bauxEntry.denominationUniteLegale
                  || ([
                    bauxEntry.sexeUniteLegale === 'M' ? 'MONSIEUR' : 'MADAME',
                    bauxEntry.prenomUsuelUniteLegale,
                    bauxEntry.nomUniteLegale,
                  ].join(' ')),
                  Adresse: <div className="flex items-center">
                    <div>
                      <RedirectionMapButtons address={address} />
                    </div>
                    <span>{address}</span>
                  </div>,
                  'Nom de la copro': <div className="flex items-center">
                    <RedirectionCoproButtons coproId={bauxEntry.idCopro} />
                    {bauxEntry.nameCopro}
                  </div>,
                  Gestionnaire: bauxEntry.gestionnaireCopro.firstname ? `${bauxEntry.gestionnaireCopro.firstname} `
                    + `${bauxEntry.gestionnaireCopro.lastname ? bauxEntry.gestionnaireCopro.lastname.toUpperCase() : ''}` : '',
                  'Date potentielle de période triennale': formatDateStr(bauxEntry.leaseEndDate),
                  'Voir le détail': <div className="flex justify-center">
                    <button className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto" onClick={() => handleDetails(index)}>
                      Voir le détail
                    </button>
                  </div>,
                };
              }) : []}
              headers={headers}
              tableClass="bg-rgpd-blue-light"
              sortableColumns={sortableColumns}
              isSearchable
              filterColumnName="Date potentielle de période triennale"
              defaultSortKey="Date potentielle de période triennale"
              defaultSortDirection="asc"
            />
            {!baux && (
              <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
            )}
            {baux && baux.length === 0 && (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucun bail commercial à échéance trouvé aux adresses de vos copros"
                  className="my-10"
                />
              </div>
            )}
          </div>
          {isPopupOpen && (
            <Popup
              isOpen
              title={renderPopupTitle(selectedRowDetails)}
              onClose={() => {
                setIsPopupOpen(false);
                setSelectedRowDetails(null);
              }}
            >
              {selectedRowDetails !== null && baux && (
                <div className="max-h-[75dvh] gap-2 overflow-y-scroll">
                  {renderNestedObject(baux[selectedRowDetails])}
                </div>
              )}
              {!baux && (
                <Skeleton count={10} height={25} width={400} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
              )}
            </Popup>
          )}
        </div>
      </div>
    </div>
  );
}

export default BauxAEcheance;
